import { Modal } from 'components/Modal/Modal'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { Button, Stack, Typography } from '@mui/material'
import { colorPrimitives } from 'components/Theme'
import { type SelectedWinnerType } from 'pages/Contests/Sweeptakes/SweepstakesWinnersPage/SweepstakesWinnersPage'
import { ActivityButton } from 'components/ActivityButton'
import { usePostSweepstakeWinnerMessage } from 'hooks/api/Sweepstake/usePostSweepstakeWinnerMessage'
import { usePostLocationSweepstakeWinnerMessage } from 'hooks/api/Sweepstake/usePostLocationSweepstakeWinnerMessage'
import { useSnackbar } from 'stores/useSnackbar'
import { useLayout } from 'hooks/useLayout'
import { type QueryKey } from '@tanstack/react-query'

interface SweepstakeNotifyWinnersProps {
  isModalOpen: boolean
  toggleIsOpen: () => void
  sweepstakeName: string
  selectedWinners: SelectedWinnerType[]
  clearSelectedWinners?: () => void
  isBulkAction?: boolean
  isLocationSweepstake?: boolean
  invalidateQueryKeys?: readonly QueryKey[]
}

export const SweepstakeNotifyWinnersModal = ({
  isModalOpen,
  toggleIsOpen,
  sweepstakeName,
  selectedWinners,
  clearSelectedWinners = () => {},
  isBulkAction = false,
  isLocationSweepstake = false,
  invalidateQueryKeys,
}: SweepstakeNotifyWinnersProps) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : ''
  const setMessage = useSnackbar((state) => state.setMessage)

  const notifyWinners = usePostSweepstakeWinnerMessage({
    invalidateQueryKeys,
    onSuccess: () => {
      setMessage(
        `${isBulkAction ? 'Winners' : 'Winner'} has been notified.`,
        'success'
      )
      clearSelectedWinners()
      toggleIsOpen()
    },
    onError: () => {
      setMessage(
        `Error notifying ${isBulkAction ? 'winners' : 'winner'}`,
        'error'
      )
    },
  })

  const notifyLocationSweepstakeWinners =
    usePostLocationSweepstakeWinnerMessage({
      invalidateQueryKeys,
      onSuccess: () => {
        setMessage(
          `${isBulkAction ? 'Winners' : 'Winner'} has been notified.`,
          'success'
        )
        clearSelectedWinners()
        toggleIsOpen()
      },
      onError: () => {
        setMessage(
          `Error notifying ${isBulkAction ? 'winners' : 'winner'}`,
          'error'
        )
      },
    })

  const handleNotifyWinners = () => {
    notifyWinners.mutate({
      sweepstakeWinnerIds: selectedWinners.map(
        (winner) => winner.sweepstakesWinner.id
      ),
    })
  }

  const handleNotifyLocationSweepstakeWinners = () => {
    notifyLocationSweepstakeWinners.mutate({
      sweepstakeWinnerIds: selectedWinners.map(
        (winner) => winner.sweepstakesWinner.id
      ),
    })
  }

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={toggleIsOpen}
      sx={{
        width: { xs: '90vw', sm: '60%' },
      }}
    >
      <ModalFullHeader
        toggleIsOpen={toggleIsOpen}
        title={isBulkAction ? 'Notify Winners' : 'Notify Winner'}
        subTitle={sweepstakeName}
      />
      <ModalFullBody heightModal={heightModal}>
        <Typography fontSize={18} fontWeight="bold" className="pb-3">
          Are you sure you want to notify{' '}
          {isBulkAction ? 'these winners' : 'this winner'} they won?
        </Typography>
        <Typography sx={{ color: colorPrimitives.gray70 }} className="pb-6">
          Selecting &quot;{isBulkAction ? 'Notify Winners' : 'Notify Winner'}
          &quot; will send a message to the winner through the Message Center of
          the J&J Gaming app, as well as an email, informing them of their win
          and providing instructions on how to claim their prize.
        </Typography>
        <Stack direction="column">
          {selectedWinners.map((winner) => {
            return (
              <Stack
                columnGap={0.5}
                direction="row"
                key={winner.sweepstakesWinner.id}
                className="pb-4"
              >
                {winner.locationName !== '???' && (
                  <Typography fontWeight="bold">
                    {winner.locationName}
                  </Typography>
                )}
                {winner.licenseNumber === undefined ||
                winner.licenseNumber === '#undefined'
                  ? `${winner.winnerName}`
                  : `(${winner.licenseNumber}): ${winner.winnerName}`}
              </Stack>
            )
          })}
        </Stack>
      </ModalFullBody>
      <ModalFullFooter
        leftFooter={
          isBulkAction && (
            <Typography
              sx={{ color: colorPrimitives.gray70 }}
              className="self-center"
            >
              Notify {selectedWinners.length} Winners
            </Typography>
          )
        }
      >
        <Button variant="text" onClick={toggleIsOpen}>
          Cancel
        </Button>
        {!isLocationSweepstake && (
          <ActivityButton
            active={notifyWinners.isPending}
            variant="contained"
            onClick={handleNotifyWinners}
          >
            {isBulkAction ? 'Notify Winners' : 'Notify Winner'}
          </ActivityButton>
        )}
        {isLocationSweepstake && (
          <ActivityButton
            active={notifyLocationSweepstakeWinners.isPending}
            variant="contained"
            onClick={handleNotifyLocationSweepstakeWinners}
          >
            {isBulkAction ? 'Notify Winners' : 'Notify Winner'}
          </ActivityButton>
        )}
      </ModalFullFooter>
    </Modal>
  )
}
